import { useEffect, useState } from 'react';
import {
  detectPlatform,
  getDeferredPrompt,
  subscribeDeferredPrompt,
  triggerInstallPrompt,
} from '../../lib/pwa';
import { idb } from '../../lib/idb';

interface Props {
  intent: 'create' | 'join';
  inviteCode?: string;
}

export function InstallGate({ intent, inviteCode }: Props) {
  const platform = detectPlatform();
  const [hasDeferred, setHasDeferred] = useState<boolean>(!!getDeferredPrompt());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (intent === 'join' && inviteCode) {
      void idb.setPendingInvite(inviteCode);
    }
  }, [intent, inviteCode]);

  useEffect(() => {
    return subscribeDeferredPrompt((p) => setHasDeferred(!!p));
  }, []);

  async function onInstall() {
    setBusy(true);
    try {
      await triggerInstallPrompt();
    } finally {
      setBusy(false);
    }
  }

  const headline = intent === 'join' ? "You're invited" : 'Install Nudge first';
  const sub = intent === 'join'
    ? "Install Nudge on your device to join — we'll bring you right back to this invite."
    : 'Nudge works best as an installed app. Install it before creating your circle.';

  return (
    <div className="mx-auto flex min-h-screen max-w-screen-sm flex-col items-center justify-center bg-base px-6 text-text-primary">
      <h1 className="text-2xl font-medium">{headline}</h1>
      <p className="mt-2 max-w-sm text-center text-sm text-text-muted">{sub}</p>

      <div className="mt-8 w-full max-w-xs">
        {platform === 'ios-safari' && (
          <ol className="space-y-3 rounded-xl border border-border-subtle bg-card p-4 text-sm text-text-primary">
            <li>1. Tap the <span className="text-text-muted">Share</span> button at the bottom of Safari.</li>
            <li>2. Choose <span className="text-text-muted">Add to Home Screen</span>.</li>
            <li>3. Open Nudge from your Home Screen{intent === 'join' ? ' — your invite will be waiting.' : '.'}</li>
          </ol>
        )}

        {(platform === 'android-chrome' || platform === 'desktop') && (
          <>
            {hasDeferred ? (
              <button
                onClick={onInstall}
                disabled={busy}
                className="w-full rounded-xl bg-sage py-3 text-base font-medium text-white disabled:opacity-50"
              >
                {busy ? 'Installing…' : 'Install Nudge'}
              </button>
            ) : (
              <div className="rounded-xl border border-border-subtle bg-card p-4 text-sm text-text-primary">
                <p>Open the browser menu and choose <span className="text-text-muted">Install app</span> or <span className="text-text-muted">Add to Home screen</span>.</p>
              </div>
            )}
          </>
        )}

        {platform === 'other' && (
          <div className="rounded-xl border border-border-subtle bg-card p-4 text-sm text-text-primary">
            <p>For the best experience, open Nudge in Chrome (Android/desktop) or Safari (iPhone/iPad) and install it from the browser menu.</p>
          </div>
        )}
      </div>
    </div>
  );
}
